  import React, { useEffect, useState, useMemo, useRef } from "react";
  import { Box, TextField, Button, TablePagination, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
  import { DataGrid } from "@mui/x-data-grid";
  import PageHeader1 from "../../components/common/PageHeader1";
  import { useDispatch, useSelector } from "react-redux";
  import { fetchAstroEarningHistory } from "../../Redux/Reducers/AstroEarningHistoryReducer";
  import { handleUnauthorized } from "../../TokenAuth/auth";
  import { formatUTCDateForDataGrid, formatUTCDate, convertISTToUTCForAPI } from "../../utils/dateUtils";
  import { useSearchParams } from "react-router-dom";
  import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

  function AstroEarningHistory() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const state = useSelector((state) => state?.AstroEarningHistoryReducer || {});
    const { data: rowsData = [], paginationDetail = {}, status = "idle" } = state;

    const didInitUrlSyncRef = useRef(false);
    const applySawLoadingRef = useRef(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
      page: getIntParam(searchParams, "page", 0),
      pageSize: getIntParam(searchParams, "pageSize", 10),
    });

    const getTodayDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const todayDate = getTodayDate();

    const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [searchType, setSearchType] = useState(getStringParam(searchParams, "searchType", "astrologerName"));
    const [appliedSearchType, setAppliedSearchType] = useState(getStringParam(searchParams, "searchType", "astrologerName"));
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [isExporting, setIsExporting] = useState(false);
    const [lastAction, setLastAction] = useState(null); 
    const isInitialMount = useRef(true);
    const lastParamsRef = useRef(null);

    const hasPendingFilterChanges =
      searchQuery.trim() !== (appliedSearchQuery || "") ||
      searchType !== (appliedSearchType || "astrologerName") ||
      (startDate || "") !== (appliedStartDate || "") ||
      (endDate || "") !== (appliedEndDate || "");

    const handleApplyFilters = () => {
      setIsApplyingFilters(true);
      applySawLoadingRef.current = false;
      setAppliedSearchQuery(searchQuery.trim());
      setAppliedSearchType(searchType);
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
      setLastAction("apply");
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    useEffect(() => {
      if (!isApplyingFilters) return;
      if (status === "loading") {
        applySawLoadingRef.current = true;
        return;
      }
      if (status !== "loading" && applySawLoadingRef.current) {
        applySawLoadingRef.current = false;
        setIsApplyingFilters(false);
      }
    }, [status, isApplyingFilters]);

    useEffect(() => {
      if (!didInitUrlSyncRef.current) {
        didInitUrlSyncRef.current = true;
        return;
      }
      const params = new URLSearchParams();
      params.set("page", String(paginationModel.page));
      params.set("pageSize", String(paginationModel.pageSize));
      setIfTruthy(params, "searchType", appliedSearchType);
      setIfTruthy(params, "search", appliedSearchQuery);
      setIfTruthy(params, "startDate", appliedStartDate);
      setIfTruthy(params, "endDate", appliedEndDate);
      setSearchParams(params, { replace: true });
    }, [
      paginationModel.page,
      paginationModel.pageSize,
      appliedSearchType,
      appliedSearchQuery,
      appliedStartDate,
      appliedEndDate,
      setSearchParams,
    ]);

    useEffect(() => {
      const page = paginationModel.page + 1;
      let limit = Number(paginationModel.pageSize);
      if (isNaN(limit)) {
        limit = 10;
      }

      const params = {
        page: page,
        limit: limit,
      };

      if (appliedStartDate && appliedEndDate) {
        params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
        params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
      }

      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        if (appliedSearchType === "astrologerName") {
          params.astrologerName = appliedSearchQuery;
        } else {
          params.mobileNumber = appliedSearchQuery;
        }
      }

      const paramsString = JSON.stringify(params);
      if (lastParamsRef.current === paramsString && !isInitialMount.current) {
        return;
      }

      lastParamsRef.current = paramsString;
      isInitialMount.current = false;

      dispatch(fetchAstroEarningHistory(params));
    }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedStartDate, appliedEndDate, appliedSearchQuery, appliedSearchType]);

    const columns = [
      {
        field: "sono",
        headerName: "S.No",
        width: 80,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: "date",
        headerName: "Date",
        width: 180,
        valueFormatter: formatUTCDateForDataGrid
      },
      { field: "astrologerName", headerName: "Astrologer Name", width: 200 },
      { field: "mobileNumber", headerName: "Mobile No.", width: 130 },
      { field: "type", headerName: "Type", width: 120 },
      {
        field: "amount",
        headerName: "Amount",
        width: 120,
        type: 'number',
        renderCell: (params) => (
          <span style={{ fontWeight: 600, color: '#2e7d32' }}>
            ₹{params.value?.toLocaleString('en-IN') || '0'}
          </span>
        )
      },
      {
        field: "duration",
        headerName: "Duration (Min)",
        width: 130,
        renderCell: (params) => {
          const seconds = params.value || 0;
          const mins = Math.floor(seconds / 60);
          const secs = seconds % 60;
          return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
      },
      {
        field: "idInfo",
        headerName: "Order/Trans. ID",
        width: 200,
        valueGetter: (params, row) => row.orderId || row.transactionId || "N/A"
      },
      { field: "description", headerName: "Description", width: 300 },
    ];

    const rows = useMemo(() => {
      const pageSize = paginationModel.pageSize;
      return rowsData?.map((item, index) => ({
        ...item,
        id: item._id || index,
        sono: index + 1 + paginationModel.page * pageSize,
      }));
    }, [rowsData, paginationModel]);

    const handleChangePage = (event, newPage) =>
      setPaginationModel((prev) => ({ ...prev, page: newPage }));

    const handleChangeRowsPerPage = (event) => {
      const value = event.target.value;
      const newSize = parseInt(value, 10);
      setPaginationModel({ page: 0, pageSize: newSize });
    };

    const handleResetFilter = () => {
      setStartDate("");
      setEndDate("");
      setAppliedStartDate("");
      setAppliedEndDate("");
      setSearchQuery("");
      setAppliedSearchQuery("");
      setSearchType("astrologerName");
      setAppliedSearchType("astrologerName");
      setLastAction(null);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleExport = async () => {
      setIsExporting(true);

      const escapeCsvCell = (cell) => {
        if (cell === null || cell === undefined) return '';
        const cellString = String(cell);
        if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
          return `"${cellString.replace(/"/g, '""')}"`;
        }
        return cellString;
      };

      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();
        
        if (appliedStartDate) params.append('startDate', appliedStartDate);
        if (appliedEndDate) params.append('endDate', appliedEndDate);
        if (appliedSearchQuery) {
          if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else {
            params.append('mobileNumber', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/astroEarningHistoryExport?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            handleUnauthorized();
          }
          throw new Error("Failed to export data");
        }

        const result = await response.json();
        const allData = result?.data || [];

        const headers = ['S.No', 'Date', 'Astrologer Name', 'Mobile No.', 'Type', 'Amount (₹)', 'Duration', 'Order ID', 'Transaction ID', 'Description'];
        const headerRow = headers.map(h => escapeCsvCell(h)).join(',');

        const dataRows = allData.map((item, index) => {
          const duration = item.duration || 0;
          const mins = Math.floor(duration / 60);
          const secs = duration % 60;
          const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;

          const row = [
            index + 1,
            formatUTCDate(item.date, { format: 'date', separator: '/' }) + ' ' + formatUTCDate(item.date, { format: 'time', showSeconds: false }),
            item.astrologerName || 'N/A',
            item.mobileNumber || 'N/A',
            item.type || 'N/A',
            item.amount || 0,
            durationStr,
            item.orderId || 'N/A',
            item.transactionId || 'N/A',
            item.description || 'N/A'
          ];
          return row.map(cell => escapeCsvCell(cell)).join(',');
        });

        const csvContent = [headerRow, ...dataRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `AstroEarningHistory-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export error:", error);
        alert(error.message || 'Export failed. Please try again.');
      } finally {
        setIsExporting(false);
      }
    };

    return (
      <div className="body d-flex">
        <div className="container-xxl">
          <PageHeader1 pagetitle="Astrologer Earning History" />

          <Box sx={{ width: '100%', mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
              <FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
                <InputLabel>Search By</InputLabel>
                <Select
                  value={searchType}
                  label="Search By"
                  onChange={(e) => setSearchType(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                  <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                </Select>
              </FormControl>
              <TextField
                placeholder={searchType === "astrologerName" ? "Enter name..." : "Enter mobile..."}
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                sx={{
                  width: { xs: '150px', sm: '180px', md: '200px' },
                  flexShrink: 0,
                  background: 'var(--card-color)',
                  borderRadius: '9px',
                  '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 10px 9px 10px' },
                }}
              />
              <TextField
                label="Start Date"
                type="date"
                variant="outlined"
                size="small"
                value={startDate || ""}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '140px', sm: '160px', md: '180px' }, flexShrink: 0 }}
              />
              <TextField
                label="End Date"
                type="date"
                variant="outlined"
                size="small"
                value={endDate || ""}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '140px', sm: '160px', md: '180px' }, flexShrink: 0 }}
              />
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={status === "loading" || isApplyingFilters || !hasPendingFilterChanges}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  height: 40,
                  px: 3,
                  "&:hover": { backgroundColor: "#1565c0" },
                }}
              >
                {isApplyingFilters ? "Applying..." : "APPLY FILTERS"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilter}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  height: 40,
                  px: 3,
                }}
              >
                RESET
              </Button>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={isExporting}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  height: 40,
                  px: 3,
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1b5e20" },
                }}
              >
                {isExporting ? <CircularProgress size={20} color="inherit" /> : "EXPORT TO EXCEL"}
              </Button>
            </Box>
          </Box>

          <div className="card mb-3">
            <div className="card-body" style={{ padding: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <TablePagination
                  component="div"
                  count={paginationDetail?.totalDocs || 0}
                  page={paginationModel.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Box>
              <Box sx={{ width: '100%', height: 600 }}>
                <DataGrid
                  columns={columns}
                  rows={rows}
                  getRowId={(row) => row.id}
                  paginationMode="server"
                  loading={status === "loading"}
                  hideFooter
                  sx={{ border: 'none' }}
                />
              </Box>
            </div>
          </div>
        </div>
      </div>
    );
  }

  export default AstroEarningHistory;
